import { useEffect, useRef, useMemo } from 'react';
import L, { LatLngExpression, LatLngTuple } from 'leaflet';
import { useMap } from 'react-leaflet';
import { resolveImageUrl } from '@service/axios-instance';

// Ensure L.ImageOverlay.Rotated is registered once on Leaflet
function registerRotatedImageOverlay() {
  const LeafletAny = L as any;
  if (LeafletAny.ImageOverlay && LeafletAny.ImageOverlay.Rotated) {
    return;
  }

  LeafletAny.ImageOverlay.Rotated = LeafletAny.ImageOverlay.extend({
    initialize: function (image: string | HTMLImageElement, topleft: LatLngExpression, topright: LatLngExpression, bottomleft: LatLngExpression, options?: any) {
      if (typeof image === 'string') {
        this._url = resolveImageUrl(image);
      } else {
        this._rawImage = image;
      }

      this._topLeft = L.latLng(topleft as any);
      this._topRight = L.latLng(topright as any);
      this._bottomLeft = L.latLng(bottomleft as any);

      L.setOptions(this, options);
    },

    onAdd: function (map: any) {
      if (!this._image) {
        this._initImage();

        if (this.options.opacity < 1) {
          this._updateOpacity();
        }
      }

      if (this.options.interactive) {
        L.DomUtil.addClass(this._rawImage, 'leaflet-interactive');
        this.addInteractiveTarget(this._rawImage);
      }

      map.on('zoomend resetview', this._reset, this);
      this.getPane().appendChild(this._image);
      this._reset();
    },

    onRemove: function (map: any) {
      map.off('zoomend resetview', this._reset, this);
      L.ImageOverlay.prototype.onRemove.call(this, map);
    },

    _initImage: function () {
      let img = this._rawImage;
      if (this._url) {
        img = L.DomUtil.create('img');
        img.style.display = 'none';

        if (this.options.crossOrigin) {
          img.crossOrigin = '';
        }

        img.src = this._url;
        this._rawImage = img;
      }
      L.DomUtil.addClass(img, 'leaflet-image-layer');

      const div = (this._image = L.DomUtil.create(
        'div',
        'leaflet-image-layer ' + (this._zoomAnimated ? 'leaflet-zoom-animated' : '')
      ));

      if (this.options.zIndex !== undefined) {
        div.style.zIndex = this.options.zIndex;
      }

      div.appendChild(img);
      div.onselectstart = (L.Util as any).falseFn;
      div.onmousemove = (L.Util as any).falseFn;

      img.onload = () => {
        this._reset();
        img.style.display = 'block';
        this.fire('load');
      };

      img.onerror = (e: any) => {
        console.error('RotatedImageOverlay failed to load:', this._url, e);
      };

      if (img.complete && (img.naturalWidth || img.width)) {
        this._reset();
        img.style.display = 'block';
      }

      if (this.options.alt) {
        img.alt = this.options.alt;
      }
    },

    _reset: function () {
      const div = this._image;
      if (!this._map || !this._rawImage) {
        return;
      }

      const pxTopLeft = this._map.latLngToLayerPoint(this._topLeft);
      const pxTopRight = this._map.latLngToLayerPoint(this._topRight);
      const pxBottomLeft = this._map.latLngToLayerPoint(this._bottomLeft);

      const pxBottomRight = pxTopRight.subtract(pxTopLeft).add(pxBottomLeft);
      const pxBounds = L.bounds([pxTopLeft, pxTopRight, pxBottomLeft, pxBottomRight]);
      if (!pxBounds || !pxBounds.min || !pxBounds.max) {
        return;
      }
      const size = pxBounds.getSize();
      const pxTopLeftInDiv = pxTopLeft.subtract(pxBounds.min);

      this._bounds = L.latLngBounds(
        this._map.layerPointToLatLng(pxBounds.min),
        this._map.layerPointToLatLng(pxBounds.max)
      );

      L.DomUtil.setPosition(div, pxBounds.min);

      div.style.width = size.x + 'px';
      div.style.height = size.y + 'px';

      const imgW = this._rawImage.naturalWidth || this._rawImage.width;
      const imgH = this._rawImage.naturalHeight || this._rawImage.height;
      if (!imgW || !imgH) {
        return;
      }

      const vectorX = pxTopRight.subtract(pxTopLeft);
      const vectorY = pxBottomLeft.subtract(pxTopLeft);

      this._rawImage.style.transformOrigin = '0 0';
      this._rawImage.style.transform =
        'matrix(' +
        vectorX.x / imgW +
        ', ' +
        vectorX.y / imgW +
        ', ' +
        vectorY.x / imgH +
        ', ' +
        vectorY.y / imgH +
        ', ' +
        pxTopLeftInDiv.x +
        ', ' +
        pxTopLeftInDiv.y +
        ')';
    },

    reposition: function (topleft: LatLngExpression, topright: LatLngExpression, bottomleft: LatLngExpression) {
      this._topLeft = L.latLng(topleft as any);
      this._topRight = L.latLng(topright as any);
      this._bottomLeft = L.latLng(bottomleft as any);
      this._reset();
    },

    setUrl: function (url: string) {
      this._url = resolveImageUrl(url);
      if (this._rawImage) {
        this._rawImage.style.display = 'none';
        this._rawImage.src = this._url;
        if (this._rawImage.complete && (this._rawImage.naturalWidth || this._rawImage.width)) {
          this._reset();
          this._rawImage.style.display = 'block';
        }
      }
      return this;
    },
  });

  LeafletAny.imageOverlay.rotated = function (
    imgSrc: string | HTMLImageElement,
    topleft: LatLngExpression,
    topright: LatLngExpression,
    bottomleft: LatLngExpression,
    options?: any
  ) {
    return new LeafletAny.ImageOverlay.Rotated(imgSrc, topleft, topright, bottomleft, options);
  };
}

// Register on load
registerRotatedImageOverlay();

export interface RotatedImageOverlayProps {
  url: string;
  topleft: LatLngTuple | [number, number];
  topright: LatLngTuple | [number, number];
  bottomleft: LatLngTuple | [number, number];
  opacity?: number;
  interactive?: boolean;
  zIndex?: number;
  onClick?: () => void;
}

export function RotatedImageOverlay({
  url,
  topleft,
  topright,
  bottomleft,
  opacity = 0.75,
  interactive = false,
  zIndex,
  onClick,
}: RotatedImageOverlayProps) {
  const map = useMap();
  const layerRef = useRef<any>(null);
  const resolvedUrl = useMemo(() => resolveImageUrl(url), [url]);

  useEffect(() => {
    registerRotatedImageOverlay();
    if (!map || !resolvedUrl || !topleft || !topright || !bottomleft) return;

    const LeafletAny = L as any;
    if (!LeafletAny.imageOverlay?.rotated) return;

    const layer = LeafletAny.imageOverlay.rotated(
      resolvedUrl,
      L.latLng(topleft[0], topleft[1]),
      L.latLng(topright[0], topright[1]),
      L.latLng(bottomleft[0], bottomleft[1]),
      { opacity, interactive, zIndex }
    );

    layer.addTo(map);
    layerRef.current = layer;

    if (onClick && interactive) {
      layer.on('click', onClick);
    }

    return () => {
      if (layerRef.current && map) {
        try {
          map.removeLayer(layerRef.current);
        } catch (e) {
          // ignore cleanup error if map already removed
        }
        layerRef.current = null;
      }
    };
  }, [map, resolvedUrl]);

  // Update url dynamically
  useEffect(() => {
    if (layerRef.current && resolvedUrl) {
      layerRef.current.setUrl(resolvedUrl);
    }
  }, [resolvedUrl]);

  // Update position dynamically
  useEffect(() => {
    if (layerRef.current && topleft && topright && bottomleft) {
      layerRef.current.reposition(
        L.latLng(topleft[0], topleft[1]),
        L.latLng(topright[0], topright[1]),
        L.latLng(bottomleft[0], bottomleft[1])
      );
    }
  }, [topleft, topright, bottomleft]);

  // Update opacity dynamically
  useEffect(() => {
    if (layerRef.current && typeof layerRef.current.setOpacity === 'function') {
      layerRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  return null;
}
